/**
 * DEFCAT P6 — minimal "add entry" dialog. Captures a name and creates an
 * entry on the DRAFT under the given parent (or root) for the given axis.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import type { CatalogueAxis } from "@aeriosiq/types";
import { useCreateEntryMutation } from "./taxonomyQueries.js";

export interface AddEntryDialogProps {
  open: boolean;
  catalogueId: string;
  versionId: string;
  axis: CatalogueAxis;
  parentEntryId: string | null;
  /**
   * DEFCAT-R2-04 — when adding a root location under an L0 reference node, the
   * new entry inherits that L0's componentGroupId so it slots under that root.
   */
  componentGroupId?: string | null;
  /** DEFCAT-R2-11 — level-aware caption, e.g. "location group". */
  levelLabel?: string;
  onClose: () => void;
}

export function AddEntryDialog({
  open,
  catalogueId,
  versionId,
  axis,
  parentEntryId,
  componentGroupId,
  levelLabel,
  onClose,
}: AddEntryDialogProps): ReactElement {
  const [name, setName] = useState("");
  const create = useCreateEntryMutation(catalogueId, versionId);

  useEffect(() => {
    if (open) {
      setName("");
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSubmit(): void {
    if (name.trim().length === 0) return;
    create.mutate(
      {
        axis,
        parentEntryId,
        name: { en: name.trim() },
        ...(componentGroupId != null ? { componentGroupId } : {}),
      },
      { onSuccess: onClose },
    );
  }

  const axisLabel =
    levelLabel && levelLabel.length > 0
      ? levelLabel
      : axis === "location"
        ? "location"
        : "type";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add {axisLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {create.isError ? (
            <Alert severity="error">Could not create the entry.</Alert>
          ) : null}
          <TextField
            label="Name (en)"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            fullWidth
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={create.isPending || name.trim().length === 0}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
